import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
// api
import ApiKey from '../../api/ApiKey';
import BaseApi from '../../api/BaseApi';
// components
import APNavBar from '../Elements/APNavBar';
import APSidebar from '../APSidebar/APSidebar';
// helpers
import { formatDate, resultInfo } from '../../helpers';
// mui
import Tooltip from '@mui/material/Tooltip';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import CreateIcon from '@mui/icons-material/Create';
// import DeleteIcon from '@mui/icons-material/Delete';
import VerifiedIcon from '@mui/icons-material/Verified';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { Box, IconButton, Modal, Pagination, Stack } from '@mui/material';
// import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
// import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
// settings
import { blockReasons, warningReasons } from './settings';

const APResumesList = () => {
  const navigate = useNavigate();
  const tokenKey = Cookies.get('token');
  const adminID = Cookies.get('adminID');
  const { t } = useTranslation('global');

  const [open, setOpen] = useState(false);
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selectAll, setSelectAll] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [checkedData, setCheckedData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [positionFrom, setPositionFrom] = useState(0);
  const [selectedBlockReasons, setSelectedBlockReasons] = useState([]);
  const [selectedWarningReasons, setSelectedWarningReasons] = useState([]);

  const handleCheckboxChange = (reason, type) => {
    const selectedReasons = type === 'block' ? selectedBlockReasons : selectedWarningReasons;
    const setSelectedReasons = type === 'block' ? setSelectedBlockReasons : setSelectedWarningReasons;

    if (selectedReasons.includes(reason)) {
      setSelectedReasons(selectedReasons.filter(r => r !== reason));
    } else {
      setSelectedReasons([...selectedReasons, reason]);
    }
  };

  const isBlockButtonActive = selectedBlockReasons.length > 0 || selectedWarningReasons.length > 0;
  const isPublishButtonActive = selectedBlockReasons.length === 0 && selectedWarningReasons.length === 0;

  const resumesPerPage = 50;

  const indexOfFirstJob = positionFrom + 1;
  const indexOfLastJob = (positionFrom + resumesPerPage) >= totalCount ? totalCount : (positionFrom + resumesPerPage);

  const totalPages = Math.ceil(totalCount / resumesPerPage);

  const handlePageChange = async page => {
    try {
      const positionNumber = page * resumesPerPage - resumesPerPage;

      setLoading(true);
      setCurrentPage(page);
      setPositionFrom(positionNumber);

      const response = await axios.post(
        BaseApi + '/admin/resumes/index',
        {
          searchQuery,
          limit: resumesPerPage,
          positionFrom: positionNumber,
        },
        {
          headers: {
            key: ApiKey,
            adminid: adminID,
            ContentType: 'application/json',
            Authorization: `Bearer ${tokenKey}`,
          },
        },
      );

      setLoading(false);
      setCheckedData([]);
      setSelectAll(true);
      setListData(response.data.response.cv_list);
      setTotalCount(response.data.response.totalCount);
      setPositionFrom(response.data.response.positionFrom);
    } catch (error) {
      setLoading(false);

      console.error('Error fetching Jobs List', error);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        BaseApi + '/admin/resumes/index',
        {
          searchQuery,
          positionFrom,
          limit: resumesPerPage,
        },
        {
          headers: {
            key: ApiKey,
            adminid: adminID,
            ContentType: 'application/json',
            Authorization: `Bearer ${tokenKey}`,
          },
        },
      );

      setLoading(false);
      setListData(response.data.response.cv_list);
      setTotalCount(response.data.response.totalCount);
      setPositionFrom(response.data.response.positionFrom);
    } catch (error) {
      setLoading(false);

      console.error('Error fetching Jobs List', error);
    }
  };

  const handleOpen = id => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedId(null);
    setOpen(false);
  };

  const handleSearchChange = e => {
    const newQuery = e.target.value.toLowerCase();

    setSearchQuery(newQuery);
  };

  const handleSearchClick = async e => {
    e.preventDefault();

    try {
      setLoading(true);

      const response = await axios.post(
        BaseApi + '/admin/resumes/index',
        {
          searchQuery,
          positionFrom: 0,
          limit: resumesPerPage,
        },
        {
          headers: {
            key: ApiKey,
            adminid: adminID,
            ContentType: 'application/json',
            Authorization: `Bearer ${tokenKey}`,
          },
        },
      );

      setLoading(false);
      setCurrentPage(1);
      setCheckedData([]);
      setSelectAll(true);
      setListData(response.data.response.cv_list);
      setTotalCount(response.data.response.totalCount);
      setPositionFrom(response.data.response.positionFrom);
    } catch (error) {
      setLoading(false);

      console.error('Error fetching Jobs List', error);
    }
  };

  const handleCheck = id => {
    if (id === 'selectAll') {
      setSelectAll(!selectAll);

      const updatedCheckedData = selectAll
        ? listData.map(item => item.id)
        : [];

      setCheckedData(updatedCheckedData);
    } else {
      if (checkedData.includes(id)) {
        setCheckedData(checkedData.filter(checkedId => checkedId !== id));
      } else {
        setCheckedData([...checkedData, id]);
      }
    }
  };

  const handleActivate = async id => {
    try {
      const confirmationResult = await Swal.fire({
        icon: 'question',
        cancelButtonText: 'No',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        title: 'Activate Resume?',
        text: 'Do you want to Activate this Resume?',
      });

      if (confirmationResult.isConfirmed) {
        const response = await axios.post(
          BaseApi + `/admin/resumes/activate/${id}`,
          null,
          {
            headers: {
              key: ApiKey,
              adminid: adminID,
              ContentType: 'application/json',
              Authorization: `Bearer ${tokenKey}`,
            },
          },
        );

        if (response.data.status === 200) {
          resultInfo('success', 'Resume Activated successfully!', 'Close');
        } else {
          resultInfo('error', response.data.message, 'Close');
        }

        getData();
        handleClose();
        setSelectedBlockReasons([]);
        setSelectedWarningReasons([]);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        confirmButtonText: 'Close',
        text: 'Could not Activate Resume',
        title: 'Failed. Please try after some time!',
      });

      console.error('Couldn\'t activate the record!', error.message);
    }
  };

  const handleDeactivate = async id => {
    try {
      const confirmationResult = await Swal.fire({
        icon: 'question',
        cancelButtonText: 'No',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        title: 'Deactivate Resume',
        text: 'Do you want to Deactivate this Resume?',
      });

      if (confirmationResult.isConfirmed) {
        const response = await axios.post(
          BaseApi + `/admin/resumes/deactivate/${id}`,
          null,
          {
            headers: {
              key: ApiKey,
              adminid: adminID,
              ContentType: 'application/json',
              Authorization: `Bearer ${tokenKey}`,
            },
          },
        );

        if (response.data.status === 200) {
          resultInfo('success', 'Resume Deactivated successfully!', 'Close');
        } else {
          resultInfo('error', response.data.message, 'Close');
        }

        getData();
        handleClose();
        setSelectedBlockReasons([]);
        setSelectedWarningReasons([]);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        confirmButtonText: 'Close',
        text: 'Could not Deactivate Resume',
        title: 'Failed. Please try after some time!',
      });

      console.error('Couldn\'t deactivate the record!', error.message);
    }
  };

  const handleVerify = async id => {
    try {
      const confirmationResult = await Swal.fire({
        icon: 'question',
        cancelButtonText: 'No',
        showCancelButton: true,
        title: 'Verify Resume?',
        confirmButtonText: 'Yes',
        text: 'Do you want to Verify this Resume?',
      });

      if (confirmationResult.isConfirmed) {
        const response = await axios.post(
          BaseApi + `/admin/resumes/verify/${id}`,
          null,
          {
            headers: {
              key: ApiKey,
              adminid: adminID,
              ContentType: 'application/json',
              Authorization: `Bearer ${tokenKey}`,
            },
          },
        );

        if (response.data.status === 200) {
          resultInfo('success', 'Resume verified successfully!', 'Close');
        } else {
          resultInfo('error', response.data.message, 'Close');
        }

        getData();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        confirmButtonText: 'Close',
        text: 'Could not Verify Resume',
        title: 'Failed. Please try after some time!',
      });

      console.error('Couldn\'t verify the record!', error.message);
    }
  };

  // const handleDelete = async slug => {
  //   try {
  //     const confirmationResult = await Swal.fire({
  //       icon: 'question',
  //       title: 'Delete Job?',
  //       cancelButtonText: 'No',
  //       showCancelButton: true,
  //       confirmButtonText: 'Yes',
  //       text: 'Do you want to Delete this Job?',
  //     });

  //     if (confirmationResult.isConfirmed) {
  //       const response = await axios.post(
  //         BaseApi + `/admin/job/admin_delete/${slug}`,
  //         null,
  //         {
  //           headers: {
  //             key: ApiKey,
  //             token: tokenKey,
  //             adminid: adminID,
  //             'Content-Type': 'application/json',
  //           },
  //         },
  //       );

  //       if (response.data.status === 200) {
  //         resultInfo('success', 'Job deleted successfully!', 'Close');
  //       } else {
  //         resultInfo('error', response.data.message, 'Close');
  //       }

  //       getData();
  //     }
  //   } catch (error) {
  //     Swal.fire({
  //       icon: 'error',
  //       confirmButtonText: 'Close',
  //       text: 'Could not Delete job',
  //       title: 'Failed. Please try after some time!',
  //     });

  //     console.error('Couldn\'t delete the record!', error.message);
  //   }
  // };

  // const handleMultipleDeactivate = async () => {
  //   try {
  //     const confirmationResult = await Swal.fire({
  //       icon: 'question',
  //       cancelButtonText: 'No',
  //       showCancelButton: true,
  //       confirmButtonText: 'Yes',
  //       title: 'Deactivate Jobs?',
  //       text: 'Do you want to Deactivate these Jobs?',
  //     });

  //     if (confirmationResult.isConfirmed) {
  //       const ids = checkedData.join(',');

  //       const response = await axios.post(
  //         BaseApi + '/admin/job/index',
  //         {
  //           idList: ids,
  //           action: 'deactivate',
  //         },
  //         {
  //           headers: {
  //             key: ApiKey,
  //             token: tokenKey,
  //             adminid: adminID,
  //             'Content-Type': 'application/json',
  //           },
  //         },
  //       );

  //       setSelectAll(true);
  //       setCheckedData([]);

  //       if (response.data.status === 200) {
  //         resultInfo('success', 'Jobs Deactivated successfully!', 'Close');
  //       } else {
  //         resultInfo('error', 'Couldn\'t Deactivate!', 'Close');
  //       }

  //       getData();
  //     }
  //   } catch (error) {
  //     Swal.fire({
  //       icon: 'error',
  //       confirmButtonText: 'Close',
  //       text: 'Could not Deactivate Jobs',
  //       title: 'Failed. Please try after some time!',
  //     });

  //     console.error('Couldn\'t deactivate the record!', error.message);
  //   }
  // };

  // const handleMultipleActivate = async () => {
  //   try {
  //     const confirmationResult = await Swal.fire({
  //       icon: 'question',
  //       cancelButtonText: 'No',
  //       showCancelButton: true,
  //       title: 'Activate Jobs?',
  //       confirmButtonText: 'Yes',
  //       text: 'Do you want to Activate these Jobs?',
  //     });

  //     if (confirmationResult.isConfirmed) {
  //       const ids = checkedData.toString();

  //       const response = await axios.post(
  //         BaseApi + '/admin/job/index',
  //         {
  //           idList: ids,
  //           action: 'activate',
  //         },
  //         {
  //           headers: {
  //             key: ApiKey,
  //             token: tokenKey,
  //             adminid: adminID,
  //             'Content-Type': 'application/json',
  //           },
  //         },
  //       );

  //       setSelectAll(true);
  //       setCheckedData([]);

  //       if (response.data.status === 200) {
  //         resultInfo('success', 'Jobs Activated successfully!', 'Close');
  //       } else {
  //         resultInfo('error', 'Couldn\'t Activate!', 'Close');
  //       }

  //       getData();
  //     }
  //   } catch (error) {
  //     Swal.fire({
  //       icon: 'error',
  //       confirmButtonText: 'Close',
  //       text: 'Could not Activate Jobs',
  //       title: 'Failed. Please try after some time!',
  //     });

  //     console.error('Couldn\'t activate the record!', error.message);
  //   }
  // };

  // const handleMultipleDelete = async () => {
  //   try {
  //     const confirmationResult = await Swal.fire({
  //       icon: 'question',
  //       title: 'Delete Jobs?',
  //       cancelButtonText: 'No',
  //       showCancelButton: true,
  //       confirmButtonText: 'Yes',
  //       text: 'Do you want to Delete these Jobs?',
  //     });

  //     if (confirmationResult.isConfirmed) {
  //       const ids = checkedData.toString();

  //       const response = await axios.post(
  //         BaseApi + '/admin/job/index',
  //         {
  //           idList: ids,
  //           action: 'delete',
  //         },
  //         {
  //           headers: {
  //             'Content-Type': 'application/json',
  //             key: ApiKey,
  //             token: tokenKey,
  //             adminid: adminID,
  //           },
  //         },
  //       );

  //       setSelectAll(true);
  //       setCheckedData([]);

  //       if (response.data.status === 200) {
  //         resultInfo('success', 'Jobs Deleted successfully!', 'Close');
  //       } else {
  //         resultInfo('error', 'Couldn\'t Delete!', 'Close');
  //       }

  //       getData();
  //     }
  //   } catch (error) {
  //     Swal.fire({
  //       icon: 'error',
  //       confirmButtonText: 'Close',
  //       text: 'Could not Delete Jobs',
  //       title: 'Failed. Please try after some time!',
  //     });

  //     console.error('Couldn\'t Delete the record!', error.message);
  //   }
  // };

  useEffect(() => {
    if (!tokenKey) {
      navigate('/admin');
    } else {
      getData();

      window.scrollTo(0, 0);
    }
  }, [tokenKey, navigate]); //eslint-disable-line

  return (
    <>
      <APNavBar />
      <div className='APBasic APManageSubadmin APResumeList'>
        <APSidebar />
        {
          loading
            ? (
              <div className='loader-container' />
            ) : (
              <div className='site-min-height'>
                <div className='breadCumb1' role='presentation'>
                  <Breadcrumbs
                    aria-label='breadcrumb'
                    separator={<NavigateNextIcon fontSize='small' />}
                  >
                    <Link
                      underline='hover'
                      color='inherit'
                      to={'/admin/admins/dashboard'}
                    >
                      Dashboard
                    </Link>
                    <Typography color='text.primary'>Resumes</Typography>
                  </Breadcrumbs>
                </div>
                {/* <div className='ManageSubAdminHeader'>
                  <h2 className=''>Resumes List</h2>
                  <button
                    className='btn navButton1 APMSbutton'
                    onClick={() => navigate('/admin/jobs/addjob')}
                  >
                    Add Job
                  </button>
                </div> */}
                <div className='manageSubadminPart1'>
                  <form onSubmit={handleSearchClick}>
                    <div className='manageSubadminPart1Sec1'>
                      <h4>
                        Search Resume by typing Resume ID or Resume Name
                      </h4>
                      <div className='APDashboardSearchBx '>
                        <input
                          type='text'
                          name='searchQuery'
                          value={searchQuery}
                          placeholder='Search..'
                          className='form-control'
                          onChange={handleSearchChange}
                        />
                      </div>
                      <button
                        type='submit'
                        className='btn button1'
                        onClick={handleSearchClick}
                      >
                        Search
                      </button>
                    </div>
                  </form>
                  <h4>
                    {t('pagination.NoofRecords')}
                    {' '}
                    {indexOfFirstJob} - {indexOfLastJob}
                    {' '}{t('pagination.of')}{' '}
                    {totalCount}
                  </h4>
                </div>
                {
                  listData == ''
                    ? (
                      <div className='appliedJobListNoData mt-5'>
                        No Records Found
                      </div>
                    ) : (
                      <>
                        <div className='manageSubadminPart2'>
                          <table className='table'>
                            <thead>
                              <tr>
                                <td className='checkBoxCol checkBxHead'>
                                  <input
                                    type='checkbox'
                                    className='tableCheckBox'
                                    onClick={() => handleCheck('selectAll')}
                                  />
                                </td>
                                <th>Jobseeker ID</th>
                                <th>Jobseeker Name</th>
                                <th>Email</th>
                                <th>Resume ID</th>
                                <th>Resume Name</th>
                                <th>Location</th>
                                <th>Category</th>
                                <th>Updated</th>
                                <th scope='col'>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {
                                listData.map(i => (
                                  <tr key={i.id}>
                                    <td className='checkBoxCol'>
                                      <input
                                        type='checkbox'
                                        className='tableCheckBox'
                                        onChange={() => handleCheck(i.id)}
                                        checked={checkedData.includes(i.id)}
                                      />
                                    </td>
                                    <td>{i.jobseeker_id}</td>
                                    <td>{i.jobseeker_name}</td>
                                    <td>{i.jobseeker_email}</td>
                                    <td>{i.id}</td>
                                    <td>
                                      {i.position}
                                      <span className='verifiedUsername'>
                                        {i.resume_verified === 1 ? <VerifiedIcon sx={{ cursor: 'default' }} /> : ''}
                                      </span>
                                    </td>
                                    <td>{i.city?.city_name}</td>
                                    <td>{i.category?.join(', ')}</td>
                                    <td>{formatDate(i.updated)}</td>
                                    <td className='APActionButton'>
                                      {
                                        i.is_active === 1
                                          ? (
                                            <button
                                              type='button'
                                              onClick={() => handleOpen(i.id)}
                                              className='btn-secondary activated'
                                            >
                                              <Tooltip title='Deactivate'>
                                                <CheckIcon />
                                              </Tooltip>
                                            </button>
                                          ) : (
                                            <button
                                              type='button'
                                              className='btn-secondary'
                                              onClick={() => handleOpen(i.id)}
                                            >
                                              <Tooltip title='Activate'>
                                                <BlockIcon />
                                              </Tooltip>
                                            </button>
                                          )
                                      }
                                      <button
                                        className='btn-primary'
                                        onClick={() => navigate(`/admin/resumes/editResume/${i.id}`)}
                                      >
                                        <Tooltip title='Edit'>
                                          <CreateIcon />
                                        </Tooltip>
                                      </button>
                                      {/* <button
                                        className='btn btn-secondary'
                                        onClick={() => handleDelete(i.id)}
                                      >
                                        <Tooltip title='Delete'>
                                          <DeleteIcon />
                                        </Tooltip>
                                      </button> */}
                                      {
                                        !i.resume_verified && (
                                          <button
                                            className='btn-primary'
                                            onClick={() => handleVerify(i.id)}
                                          >
                                            <Tooltip title='Verify Now'>
                                              <VerifiedIcon />
                                            </Tooltip>
                                          </button>
                                        )
                                      }
                                      <button
                                        className='btn-primary'
                                        onClick={() => navigate(`/admin/resumes/${i.id}`)}
                                      >
                                        <Tooltip title='View'>
                                          <RemoveRedEyeIcon />
                                        </Tooltip>
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              }
                            </tbody>
                          </table>
                        </div>
                        <div className='manageSubadminpart3'>
                          <div className='functionalityButton'>
                            <button
                              disabled
                              type='button'
                              className='btn button2'
                              // onClick={() => handleMultipleActivate()}
                            >
                              ACTIVATE
                            </button>
                            <button
                              disabled
                              type='button'
                              className='btn button2'
                              // onClick={() => handleMultipleDeactivate()}
                            >
                              DEACTIVATE
                            </button>
                            <button
                              disabled
                              type='button'
                              className='btn button1'
                              // onClick={() => handleMultipleDelete()}
                            >
                              DELETE
                            </button>
                          </div>
                          <div className='mb-5 pagination'>
                            <Stack spacing={2}>
                              <Pagination
                                color='primary'
                                boundaryCount={2}
                                count={totalPages}
                                page={currentPage}
                                onChange={(event, page) => handlePageChange(page)}
                              />
                            </Stack>
                          </div>
                        </div>
                      </>
                    )
                }
              </div>
            )}
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby='modal-modal-title'
          className='modalMain APResumeListModal'
          aria-describedby='modal-modal-description'
        >
          <Box
            className='modal adminModal modal-content'
            sx={{ width: '100%', maxWidth: '343px', maxHeight: 'fit-content' }}
          >
            <IconButton
              onClick={handleClose}
              className='close-button'
              style={{ top: 10, right: 10, position: 'absolute' }}
            >
              &times;
            </IconButton>
            <Box
              sx={{ mt: 3 }}
              id='modal-modal-description'
            >
              <div>
                <div className='blocksBox'>
                  <h2>Блокувати через:</h2>
                  {
                    blockReasons.map((reason, index) => (
                      <label key={index}>
                        <input
                          type='checkbox'
                          checked={selectedBlockReasons.includes(reason)}
                          onChange={() => handleCheckboxChange(reason, 'block')}
                        />
                        {reason}
                      </label>
                    ))
                  }
                </div>
                <div className='warningsBox'>
                  <h2>Попередити про:</h2>
                  {
                    warningReasons.map((reason, index) => (
                      <label key={index}>
                        <input
                          type='checkbox'
                          checked={selectedWarningReasons.includes(reason)}
                          onChange={() => handleCheckboxChange(reason, 'warning')}
                        />
                        {reason}
                      </label>
                    ))
                  }
                </div>
                <div className='buttons'>
                  <button
                    disabled={!isPublishButtonActive}
                    className='btn-secondary activated'
                    onClick={() => handleActivate(selectedId)}
                  >
                    <CheckIcon /> Активувати
                  </button>
                  <button
                    className='btn-secondary'
                    disabled={!isBlockButtonActive}
                    onClick={() => handleDeactivate(selectedId)}
                  >
                    <BlockIcon /> Блокувати
                  </button>
                </div>
              </div>
            </Box>
          </Box>
        </Modal>
      </div>
    </>
  );
};

export default APResumesList;
